import { SearchDestination, SearchSuggestion } from '../../../components/SearchForm/types';
import { getDOBFormat } from '../../../utils/date';
import { GetOrganizationMembersParams } from '../api/useGetOrganizationMembers';
import { OrganizationMemberList } from '../types';
import getMemberUrl from './getMemberUrl';

/**
 * Rows in the dropdown. Also the per-list fetch size, so a term that only matches one list can
 * still fill the box.
 */
export const SUGGESTION_LIMIT = 5;

/**
 * [HWM-23296] The member lists the header search covers. Names match the tab labels, and the
 * non-default ones are shown as a per-row tag so a result is always attributable to its list.
 * The set is deliberately independent of the open tab: the whole point of searching every list is
 * that the member you want may live in a list you are not looking at. Monthly Check-ins is
 * intentionally absent — it is a scheduling view over the active list, not a fourth population.
 *
 * Active is the default list and sends no `list_version`, mirroring the list page.
 */
export const MEMBER_LIST_VERSIONS = {
    Active: undefined, // the default list — sends and carries no `list_version`
    Discharged: 'discharged_and_cancelled',
    Disengaged: 'disengaged'
} as const;

export type MemberSuggestionList = keyof typeof MEMBER_LIST_VERSIONS;

const MEMBER_LISTS = Object.keys(MEMBER_LIST_VERSIONS) as MemberSuggestionList[];

/** The list a `list_version` names. Anything else — absent, `active`, check-ins — is Active. */
export const getMemberList = (listVersion?: string | null): MemberSuggestionList =>
    MEMBER_LISTS.find((list) => MEMBER_LIST_VERSIONS[list] === listVersion) ?? 'Active';

/**
 * Every list, in the order its results fill the dropdown: the one the user is viewing first, since
 * that's where they most likely expect the match, then the rest in their default order.
 */
export const orderMemberLists = (viewed: MemberSuggestionList): MemberSuggestionList[] => [
    viewed,
    ...MEMBER_LISTS.filter((list) => list !== viewed)
];

/**
 * One list's suggestion query. `heroOrgIds` is undefined for single-org logins so useRequest
 * injects the active org as a scalar — an empty array would be serialized away entirely by
 * buildQueryParams and send an org-less request.
 */
export const buildMemberSuggestionQuery = (
    search: string,
    listVersion: string | undefined,
    heroOrgIds: string[] | undefined
): GetOrganizationMembersParams => ({
    search,
    limit: SUGGESTION_LIMIT,
    ...(listVersion && { list_version: listVersion }),
    ...(heroOrgIds && { hero_organization_id: heroOrgIds })
});

export const MEMBERS_PATH = '/members';

/**
 * The list route a full search should land on for a result from this list. Switching lists starts
 * from a clean query, the same way the tabs do (see `tabParams.ts`) — the header Location filter
 * survives anyway, because ListFilter restores it from its persisted copy once its param is gone.
 *
 * Active's `null` says the key must be *absent*, which is what lets the controller tell that list
 * apart from "any list" — otherwise a search run from the Disengaged tab with an active top result
 * would never leave that tab.
 */
const getShowAllTo = (list: MemberSuggestionList): SearchDestination => ({
    pathname: MEMBERS_PATH,
    params: { list_version: MEMBER_LIST_VERSIONS[list] ?? null }
});

// Mirrors the list rows: a discharged member that was readmitted opens its new record, and
// discharged/readmitted rows are always addressed by member id (DischargedMemberList/ListItem).
// Active and Disengaged both use the default hero-user-first addressing.
const getSuggestionUrl = (member: OrganizationMemberList, list: MemberSuggestionList) => {
    const dischargedTarget = member.readmitted_to || member.member_id;

    return list === 'Discharged' && dischargedTarget
        ? getMemberUrl(null, dischargedTarget)
        : getMemberUrl(member.hero_user_id, member.member_id);
};

/**
 * Maps one list's response into dropdown rows. All three lists share the /members response shape,
 * so a single mapper covers them — only the URL rule and the tag differ.
 */
export const toMemberSuggestions = (
    members: OrganizationMemberList[] | undefined,
    list: MemberSuggestionList,
    providerNames: Record<string, string>,
    isMultiOrg: boolean
): SearchSuggestion[] => {
    const showAllTo = getShowAllTo(list);
    // Active is the list users assume they are searching, so only the other two are called out —
    // tagging every row would be noise.
    const tag = list === 'Active' ? undefined : list;

    return (members ?? []).map((member) => {
        const name = [member.first_name, member.last_name].filter(Boolean).join(' ').trim();
        const dob = getDOBFormat(member.date_of_birth, 'MM/dd/yyyy');
        const subtitle = [member.email, dob && `DOB: ${dob}`].filter(Boolean).join(' | ');
        const key = member.hero_user_id || member.member_id || member.email || name;
        const providerName = member.hero_organization_id
            ? providerNames[member.hero_organization_id]
            : undefined;

        return {
            // Scoped by list as well as org: a readmitted member legitimately appears in both
            // Active and Discharged, and member/user ids are not unique across organizations.
            // Either collision would hand React the same key for two different rows.
            id: `${list}:${member.hero_organization_id ?? ''}:${key}`,
            title: name || member.email || 'Unknown member',
            subtitle: subtitle || undefined,
            footnote: member.device_info?.device_serial ?? undefined,
            tag,
            // Provider attribution only for multi-org logins; single-tenant keeps state.
            aside: (isMultiOrg ? providerName : undefined) ?? member.state ?? undefined,
            heroOrgId: member.hero_organization_id ?? undefined,
            to: getSuggestionUrl(member, list),
            showAllTo
        };
    });
};
